import { Plus, Trash2 } from 'lucide-react'
import { Tooltip } from '@/components/ui/Tooltip'
import { parseAmountInput, toDecimal } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { draftSubtotal, emptyGroup, emptyLine, groupSubtotal, parseAmount, sharesFor } from '@/lib/recurring-draft'
import type { GroupDraft, LineDraft } from '@/lib/recurring-draft'
import { cn } from '@/lib/utils'

const GRID = 'grid grid-cols-[repeat(3,minmax(0,1fr))_auto] gap-2 sm:grid-cols-[1fr_5rem_6rem_6.5rem_4.5rem_auto]'

/** Bedragen staan als twee decimalen in de database; zo laat het veld dat ook zien. */
function normalizeAmount(value: string): string {
  return value.trim() === '' ? '' : parseAmount(value)
}

/**
 * Alleen wat er op de factuur komt: koppen, regels en bedragen. Wie welk deel
 * krijgt staat op het tabblad Verdeling; hier is dat een leesbaar signaal met
 * een sprong ernaartoe, zodat de twee lagen niet meer door elkaar lopen.
 */
export function ContentTab({
  groups,
  onChange,
  onOpenDistribution,
  readonly,
}: {
  groups: GroupDraft[]
  onChange: (next: GroupDraft[]) => void
  onOpenDistribution: (lineKey: string) => void
  readonly: boolean
}) {
  const patchGroup = (index: number, patch: Partial<GroupDraft>) =>
    onChange(groups.map((group, i) => (i === index ? { ...group, ...patch } : group)))

  return (
    <div className="space-y-3">
      {groups.map((group, index) => (
        <GroupBlock
          key={group.key}
          group={group}
          readonly={readonly}
          onChange={(patch) => patchGroup(index, patch)}
          onRemove={() => onChange(groups.filter((_, i) => i !== index))}
          onOpenDistribution={onOpenDistribution}
        />
      ))}

      <div className="flex items-center justify-between gap-3 pt-1">
        {readonly ? <span /> : (
          <button type="button" className="btn-secondary h-8 px-2.5 text-sm" onClick={() => onChange([...groups, emptyGroup('')])}>
            <Plus className="h-3.5 w-3.5" /> Groep
          </button>
        )}
        <div className="flex items-baseline gap-3">
          <span className="label-caps">Subtotaal reeks</span>
          <span className="money text-sm font-semibold text-ink">{formatEuros(draftSubtotal(groups) / 100)}</span>
        </div>
      </div>
    </div>
  )
}

function GroupBlock({
  group,
  readonly,
  onChange,
  onRemove,
  onOpenDistribution,
}: {
  group: GroupDraft
  readonly: boolean
  onChange: (patch: Partial<GroupDraft>) => void
  onRemove: () => void
  onOpenDistribution: (lineKey: string) => void
}) {
  const patchLine = (index: number, patch: Partial<LineDraft>) => {
    const next = group.lines.map((line, i) => {
      if (i !== index) return line
      const merged = { ...line, ...patch }
      if (('quantity' in patch || 'unitPrice' in patch) && merged.quantity.trim() !== '' && merged.unitPrice.trim() !== '') {
        merged.amount = toDecimal((parseAmountInput(merged.quantity) ?? 0) * (parseAmountInput(merged.unitPrice) ?? 0))
      }
      return merged
    })
    onChange({ lines: next })
  }

  return (
    <div className="overflow-hidden rounded-lg border border-hairline">
      <div className="flex items-center gap-2 border-b border-hairline bg-fill-1 px-3 py-2">
        <input
          className="flex-1 bg-transparent text-sm font-semibold text-ink outline-hidden placeholder:font-normal placeholder:text-ink-faint"
          value={group.heading}
          onChange={(e) => onChange({ heading: e.target.value })}
          placeholder="Kop op de factuur"
          aria-label="Kop van de groep"
          disabled={readonly}
        />
        <span className="money text-sm text-ink-soft">{formatEuros(groupSubtotal(group) / 100)}</span>
        <button
          type="button"
          className="btn-ghost h-7 w-7 p-0 text-ink-faint"
          aria-label="Groep verwijderen"
          disabled={readonly}
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className={cn(GRID, 'hidden border-b border-hairline px-3 py-1.5 sm:grid')}>
        <span className="label-caps">Omschrijving</span>
        <span className="label-caps text-right">Aantal</span>
        <span className="label-caps text-right">Stukprijs</span>
        <span className="label-caps text-right">Bedrag</span>
        <span className="label-caps text-right">Verdeling</span>
        <span className="w-8" />
      </div>

      <div className="divide-y divide-hairline">
        {group.lines.map((line, index) => (
          <LineRow
            key={line.key}
            line={line}
            follows={(line.ownDistribution ?? []).filter((s) => s.beneficiary !== '').length === 0}
            covered={sharesFor(group, line).length > 0}
            readonly={readonly}
            onChange={(patch) => patchLine(index, patch)}
            onRemove={() => onChange({ lines: group.lines.filter((_, i) => i !== index) })}
            onOpenDistribution={() => onOpenDistribution(line.key)}
          />
        ))}
      </div>

      {readonly ? null : (
        <button
          type="button"
          className="flex w-full items-center gap-1.5 border-t border-hairline px-3 py-2 text-sm font-medium text-accent-700 hover:bg-fill-1"
          onClick={() => onChange({ lines: [...group.lines, emptyLine()] })}
        >
          <Plus className="h-3.5 w-3.5" /> Regel
        </button>
      )}
    </div>
  )
}

function DistributionCell({
  follows,
  covered,
  description,
  onClick,
}: {
  follows: boolean
  covered: boolean
  description: string
  onClick: () => void
}) {
  const label = !covered ? 'script' : follows ? 'groep' : 'eigen'
  const hint = !covered
    ? 'Nog geen vastgelegde verdeling; het verdeel-script van de klant bepaalt dit.'
    : follows
      ? 'Volgt de verdeling van de groep.'
      : 'Deze regel heeft een eigen verdeling.'

  return (
    <Tooltip content={hint} tapToToggle={false}>
      <button
        type="button"
        onClick={onClick}
        aria-label={`Verdeling van "${description || 'deze regel'}" bekijken`}
        className={cn(
          'h-8 rounded-md px-1.5 text-right text-xs hover:bg-fill-2',
          follows || !covered ? 'text-ink-faint' : 'font-medium text-accent-700',
        )}
      >
        {label}
      </button>
    </Tooltip>
  )
}

function LineRow({
  line,
  follows,
  covered,
  readonly,
  onChange,
  onRemove,
  onOpenDistribution,
}: {
  line: LineDraft
  follows: boolean
  covered: boolean
  readonly: boolean
  onChange: (patch: Partial<LineDraft>) => void
  onRemove: () => void
  onOpenDistribution: () => void
}) {
  return (
    <div className="px-3 py-2">
      <div className={GRID}>
        <input
          className="field col-span-3 h-8 py-0 text-sm sm:col-span-1"
          value={line.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Omschrijving"
          disabled={readonly}
        />
        <input
          className="field num h-8 py-0 text-right text-sm"
          inputMode="decimal"
          value={line.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          placeholder="Aantal"
          disabled={readonly}
        />
        <input
          className="field num h-8 py-0 text-right text-sm"
          inputMode="decimal"
          value={line.unitPrice}
          onChange={(e) => onChange({ unitPrice: e.target.value })}
          onBlur={(e) => onChange({ unitPrice: normalizeAmount(e.target.value) })}
          placeholder="Stukprijs"
          disabled={readonly}
        />
        <input
          className="field num h-8 py-0 text-right text-sm"
          inputMode="decimal"
          value={line.amount}
          onChange={(e) => onChange({ amount: e.target.value })}
          onBlur={(e) => onChange({ amount: normalizeAmount(e.target.value) })}
          placeholder="Bedrag"
          disabled={readonly}
        />
        <div className="flex items-center justify-end">
          <DistributionCell follows={follows} covered={covered} description={line.description} onClick={onOpenDistribution} />
        </div>
        <button
          type="button"
          className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint"
          aria-label="Regel verwijderen"
          disabled={readonly}
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
